/**
 * Bid Parser Utility (v2.3 - Updated comment parsing)
 *
 * Parses bid text that may contain comments (with or without parentheses).
 * Used by AuctionTable to display bids with popup comments.
 *
 * Examples:
 * - "1NT (15-17 HCP)" -> { bid: "1NT", comment: "15-17 HCP" }
 * - "1NT 15-17 HCP" -> { bid: "1NT", comment: "15-17 HCP" }
 * - "Pass" -> { bid: "Pass", comment: null }
 * - "2H weak" -> { bid: "2H", comment: "weak" }
 * - "2C strong opening" -> { bid: "2C", comment: "strong opening" }
 */

export interface ParsedBid {
  bid: string;
  comment: string | null;
}

// Valid bid patterns (case-insensitive):
// - Level bids: 1C, 2D, 3H, 4S, 1NT, 2NT, etc.
// - Special calls: Pass, P, XX, X, Redouble, Double, Rdbl, Dbl (longer patterns first!)
// - Markers: -, ?
// NOTE: XX must come before X, Redouble before Double, Rdbl before Dbl
// because regex alternation matches the first alternative that succeeds
const BID_PATTERN = /^([1-7](?:C|D|H|S|NT)|Pass|P|XX|X|Redouble|Double|Rdbl|Dbl|-|\?)/i;

/**
 * Parses a bid string to extract the bid and any comment.
 * Comments can be in parentheses or simply text after the bid.
 *
 * @param bidText - The full bid text (e.g., "1NT (15-17 HCP)" or "1NT 15-17 HCP")
 * @returns ParsedBid with bid and optional comment
 */
export function parseBidWithComment(bidText: string): ParsedBid {
  if (!bidText || typeof bidText !== 'string') {
    return { bid: bidText || '', comment: null };
  }

  const trimmed = bidText.trim();

  // First, try to match parenthetical comment pattern: "bid (comment)"
  const parenMatch = trimmed.match(/^([^(]+)\s*\((.+)\)$/);
  if (parenMatch) {
    const bid = parenMatch[1]?.trim() || '';
    const comment = parenMatch[2]?.trim() || null;
    return { bid, comment };
  }

  // Second, try to match bid followed by any text as comment
  const bidMatch = trimmed.match(BID_PATTERN);
  if (bidMatch) {
    const bid = bidMatch[1] || '';
    const remainder = trimmed.slice(bid.length).trim();

    if (remainder) {
      return { bid, comment: remainder };
    }
    return { bid, comment: null };
  }

  // No recognized bid pattern, return as-is
  return { bid: trimmed, comment: null };
}

/**
 * Checks if a bid has a comment attached.
 *
 * @param bidText - The bid text to check
 * @returns true if the bid contains a comment (parenthetical or otherwise)
 */
export function bidHasComment(bidText: string): boolean {
  if (!bidText || typeof bidText !== 'string') {
    return false;
  }
  const { comment } = parseBidWithComment(bidText);
  return comment !== null;
}
